// frontend/src/pages/EstimatePacking.jsx
import { useEffect, useRef, useState } from "react";
import Page from "../ui/Page";
import { Button } from "../ui/UIComponents";

export default function EstimatePacking() {
  // UI state
  const [ui, setUi] = useState("normal"); // normal | minimized | fullscreen
  const messagesRef = useRef(null);

  // Chat state
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      html: `
        <div><strong>🤖 Tiểu Miu Lê:</strong></div>
        <div>Rất vui được gặp bạn! Hãy nhập yêu cầu đóng gói (ví dụ):</div>
        <ol style="padding-left:20px;margin:6px 0 0">
          <li><code>PIR 1240x2800 x12</code></li>
          <li><code>RB 2500x2200 x2</code></li>
        </ol>
      `,
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  // Auto scroll on new messages
  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, loading]); // same behavior as before :contentReference[oaicite:3]{index=3}

  // UI toggles (preserve original)
  function cycleUi() {
    setUi((s) =>
      s === "normal" ? "fullscreen" : s === "fullscreen" ? "minimized" : "normal"
    );
  }

  // Helpers
  function pushUser(text) {
    setMessages((m) => [
      ...m,
      { role: "user", html: `<strong>Bạn:</strong><div>${escapeHtml(text)}</div>` },
    ]);
  }
  function pushAssistant(html) {
    setMessages((m) => [...m, { role: "assistant", html }]);
  }
  function escapeHtml(s) {
    return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }

  // Submit (kept identical except the download bubble uses .btn) :contentReference[oaicite:4]{index=4}
  async function handleSubmit(e) {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || loading) return;

    pushUser(text);
    setInput("");
    setLoading(true);

    try {
      const r = await fetch("/api/packestimate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      const data = await r.json();

      if (!data.ok) {
        pushAssistant(
          `<strong>🤖 Tiểu Miu Lê:</strong><div style="color:#c1121f">⚠️ ${escapeHtml(
            data.error || "Không thể phân tích đầu vào."
          )}</div>`
        );
        return;
      }

      // Summary bubble
      pushAssistant(`
        <strong>🤖 Tiểu Miu Lê:</strong>
        <div style="white-space:pre-wrap;margin-top:6px;border:1px solid #eee;background:#f7f7f7;border-radius:6px;padding:8px 10px;font-family:ui-monospace,Menlo,Consolas,monospace;font-size:13px">
${escapeHtml(data.summary)}
        </div>
      `);

      // One card per crate (unchanged layout) :contentReference[oaicite:5]{index=5}
      for (const c of data.crates) {
        pushAssistant(`
          <strong>🤖 Tiểu Miu Lê:</strong>
          <div class="box" style="margin-top:6px">
            <div style="font-weight:600;margin-bottom:6px">Thùng #${c.crateNo}</div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;font-size:14px">
              <div style="opacity:.7">Kích thước</div><div>${c.packLength} × ${c.packWidth} × ${c.packHeight} mm</div>
              <div style="opacity:.7">Trọng lượng</div><div>${c.grossWeight} kg</div>
              <div style="opacity:.7">✈️ Air Vol</div><div>${c.volAir} kg</div>
              <div style="opacity:.7">🚛 Sea Vol</div><div>${c.volSea} kg</div>
            </div>
          </div>
        `);
      }

      // Download button bubble → use global `.btn` (no inline green style) :contentReference[oaicite:6]{index=6}
      pushAssistant(`
        <button id="dl-btn" class="btn" style="margin-top:6px">📥 Tải TXT</button>
      `);

      // attach click after DOM paints
      setTimeout(() => {
        const root = messagesRef.current;
        const btn = root?.querySelector("#dl-btn");
        if (btn) btn.onclick = () => handleDownload(data);
      }, 0);
    } catch (err) {
      pushAssistant(
        `<strong>🤖 Tiểu Miu Lê:</strong><div style="color:#c1121f">⚠️ ${escapeHtml(
          err.message
        )}</div>`
      );
    } finally {
      setLoading(false);
    }
  }

  // TXT download (same logic; uses /pack_text.json and EWP_EXPORT) :contentReference[oaicite:7]{index=7}
  async function handleDownload(result) {
    try {
      const resp = await fetch("/pack_text.json");
      const tpl = await resp.json();

      const now = new Date().toLocaleString("vi-VN");
      const map = { timestamp: now, ts: Date.now() };

      // Build lines (match original behavior)
      const lines = (tpl.header || []).map((line) => fill(line, map));
      for (const c of result.crates) {
        const local = {
          ...map,
          crateNo: c.crateNo,
          packLength: c.packLength,
          packWidth: c.packWidth,
          packHeight: c.packHeight,
          grossWeight: c.grossWeight,
          volAir: c.volAir,
          volSea: c.volSea,
        };
        lines.push(fill(tpl.crateLine, local));
      }
      (tpl.footer || []).forEach((line) => lines.push(fill(line, map)));

      const finalTpl = { filename: tpl.filename, header: lines };
      if (window?.EWP_EXPORT?.downloadTxt) {
        window.EWP_EXPORT.downloadTxt(map, finalTpl);
      } else {
        alert("EWP_EXPORT not available");
      }
    } catch (err) {
      alert("Template load failed: " + err.message);
    }
  }

  function fill(str, map) {
    return String(str || "").replace(/\{\{(\w+)\}\}/g, (_, k) =>
      k in map ? String(map[k]) : ""
    );
  }

  return (
    <Page>
      {/* Scoped CSS (trimmed but preserves fullscreen/minimize behavior) */}
      <style>{`
        .ep-container { max-width: 720px; width: 95%; margin: 0 auto; border:1px solid #ccc; border-radius:10px; box-shadow:0 0 8px rgba(0,0,0,.08); background:#fff; position:relative; transition:all .2s ease; }
        .ep-container.normal { height: 60vh; min-height: 360px; max-height: 600px; }
        .ep-container.minimized { height: 50px; }
        .ep-container.fullscreen { position: fixed; top:0; left:0; right:0; margin:0 auto; width:100%; max-width:1024px; height:100vh; border-radius:0; z-index:9999; padding-left:12px; padding-right:12px; box-sizing:border-box; }
        .ep-min-btn { position:absolute; top:6px; right:10px; border:none; background:none; font-size:18px; font-weight:bold; cursor:pointer; color:#888; z-index:2; }
        .ep-bodywrap { display:flex; flex-direction:column; height:100%; }
        .ep-container.minimized .ep-bodywrap { display:none; }
        .ep-messages { flex:1; padding:12px; overflow-y:auto; font-size:15px; background:#fff; }
        .ep-msg { margin:10px 0; padding:10px 14px; border-radius:8px; border:1px solid #ddd; background:#ffffff; white-space:pre-wrap; }
        .ep-msg.assistant { background:#eef6ff; }
        .ep-input { display:flex; gap:8px; padding:10px; border-top:1px solid #eee; background:#fefefe; }
        .ep-textarea { flex:1; padding:10px 12px; border:1px solid #ccc; border-radius:6px; min-height:44px; max-height:140px; resize:none; line-height:1.4; font-size:14px; overflow:auto; }
        @media (max-width: 840px) { .ep-container.normal { height: 70vh; } }
      `}</style>

      <main className="container">
        <h2 style={{ marginBottom: 12 }}>Estimate Packing</h2>

        <div className={`ep-container ${ui}`}>
          <button
            className="ep-min-btn"
            onClick={cycleUi}
            title={
              ui === "normal" ? "To fullscreen" : ui === "fullscreen" ? "Minimize" : "Restore"
            }
          >
            {ui === "normal" ? "🔳" : ui === "fullscreen" ? "🗕" : "➕"}
          </button>

          <div className="ep-bodywrap">
            {/* Messages */}
            <div className="ep-messages" ref={messagesRef}>
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`ep-msg ${m.role === "assistant" ? "assistant" : ""}`}
                  dangerouslySetInnerHTML={{ __html: m.html }}
                />
              ))}
              {loading && <div className="ep-msg assistant ep-thinking">Đang tính…</div>}
            </div>

            {/* Input row */}
            <form className="ep-input" onSubmit={handleSubmit}>
              <textarea
                className="ep-textarea"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ví dụ: PIR 1240x2800 x12; RB 2500x2200 x2"
              />
              <Button type="submit" disabled={loading}>
                Gửi / Send
              </Button>
            </form>
          </div>
        </div>
      </main>
    </Page>
  );
}
